import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Snackbar, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import { useTranslation } from 'react-i18next';
import {
  CLIENT_PLOTLYJS_VERSION, DASHBOARD_MOCK_MODE, editOps, getCapabilities, getDashboard, getWidgetData,
  diagnoseWidget, postRepairWidget, postUxEdits, revertToVersion, widgetUsesControl,
} from '../../utilityFunctions/dashboardApi';
import DashboardGrid from './DashboardGrid';
import WidgetFrame from './WidgetFrame';
import ControlsBar from './ControlsBar';
import VersionsDrawer from './VersionsDrawer';
import { RenameDialog, ShareToggle } from './DashboardMetaControls';
import NlEditBox from './nl/NlEditBox';
import RunProgressPanel from './nl/RunProgressPanel';
import ProvenanceDrawer from './nl/ProvenanceDrawer';
import WidgetRepairDialog, { emptyDraft } from './nl/WidgetRepairDialog';
import AddWidgetDialog from './nl/AddWidgetDialog';
import useRunPolling from './nl/useRunPolling';
import { CONTENT_TOP_OFFSET } from '../../layout/layoutMetrics';

const THEME = 'light';

// The navbar is position:fixed, so the page leaves its own top offset (same idiom
// as NlqChatWorkspace / FederationWorkspace). minWidth 0 keeps a wide child (SQL,
// long titles) from widening the page past the viewport.
const PAGE_SX = {
  px: { xs: 1, md: 3 }, pt: CONTENT_TOP_OFFSET, pb: 2, bgcolor: '#f7f7fa', minHeight: '100vh',
  boxSizing: 'border-box', minWidth: 0, maxWidth: '100%',
};
const LAYOUT_DEBOUNCE_MS = 800;

const same = (a, b) => JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

/** Effective control values: viewer-local overrides on top of the spec defaults. */
const effectiveControls = (spec, overrides) => {
  const out = {};
  ((spec && spec.global_controls) || []).forEach((c) => {
    out[c.control_id] = has(overrides, c.control_id) ? overrides[c.control_id] : (c.default === undefined ? null : c.default);
  });
  return out;
};

const majorMinor = (v) => (v ? String(v).split('.').slice(0, 2).join('.') : null);

/**
 * Dashboard viewer/editor shell.
 *
 * Data flow: the spec is loaded first, then each widget's data is fetched in
 * parallel and rendered as it arrives (progressive). Every persisted gesture is
 * an EditOp[] POSTed to /edits; the spec is only ever replaced by the server's
 * response (never patched locally). Edits are serialized so the base version
 * is always the latest one we know; a 409 means someone else moved the
 * dashboard on, so we reload.
 */
export default function DashboardView({ loginUser }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const jwt = loginUser && loginUser.jwtToken;

  const [dash, setDash] = React.useState(null); // { dashboard, version, spec, can_edit }
  const [loadState, setLoadState] = React.useState({ loading: true, error: null });
  const [viewingVersionId, setViewingVersionId] = React.useState(null);
  const [capabilities, setCapabilities] = React.useState(null);
  const [overrides, setOverrides] = React.useState({});
  const [widgetData, setWidgetData] = React.useState({});
  const [editPending, setEditPending] = React.useState(false);
  const [snack, setSnack] = React.useState(null);
  const [gridResetKey, setGridResetKey] = React.useState(0);
  const [versionsOpen, setVersionsOpen] = React.useState(false);
  const [versionsRefreshKey, setVersionsRefreshKey] = React.useState(0);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [versionWarningDismissed, setVersionWarningDismissed] = React.useState(false);
  // F2: NL generation / edit runs (polled by RunProgressPanel) and the provenance drawer.
  const location = useLocation();
  const [activeRunId, setActiveRunId] = React.useState(null);
  const [provenanceWidget, setProvenanceWidget] = React.useState(null);
  // Post-generation HITL: single-widget repair (polled inline) and NL "add widget" (an NL edit run).
  const [repairDialog, setRepairDialog] = React.useState(null); // { widgetId, mode: 'guided' | 'expert' }
  const repairDialogWidgetId = repairDialog && repairDialog.widgetId;
  const [repairRun, setRepairRun] = React.useState(null); // { widgetId, runId, draft }
  const [repairFailures, setRepairFailures] = React.useState({}); // widgetId → { stage, note, lastSql, diagnosis }
  const [repairDrafts, setRepairDrafts] = React.useState({}); // widgetId → dialog draft of the last attempt
  const [repairCheckpoint, setRepairCheckpoint] = React.useState(null); // clarification while AWAITING_HITL
  const [diagnoses, setDiagnoses] = React.useState({}); // widgetId → { loading, data } (lazy POST …/diagnose)
  const diagnosisRequestedRef = React.useRef(new Set());
  const [repairError, setRepairError] = React.useState(null);
  const [knowledgeSavedFor, setKnowledgeSavedFor] = React.useState(null);
  const [staleVersionId, setStaleVersionId] = React.useState(null); // set by a 409 on repair / add widget
  const [addWidgetOpen, setAddWidgetOpen] = React.useState(false);
  const [activeRunTitle, setActiveRunTitle] = React.useState(null);
  // While an NL generation runs no version exists yet: GET /{id} is 404, which is not an error then.
  const runFromUrl = new URLSearchParams(location.search || '').get('run');
  const runFromUrlRef = React.useRef(runFromUrl);
  runFromUrlRef.current = runFromUrl;

  // Refs mirror the latest state for async callbacks (debounced layout, queued edits).
  const dashRef = React.useRef(null);
  const overridesRef = React.useRef({});
  const viewingRef = React.useRef(null);
  const seqRef = React.useRef({});
  const mountedRef = React.useRef(true);
  const editChainRef = React.useRef(Promise.resolve());
  const repairRunRef = React.useRef(null);
  const addWidgetRunRef = React.useRef(null);
  const pendingLayoutRef = React.useRef({});
  const layoutTimerRef = React.useRef(null);

  dashRef.current = dash;
  repairRunRef.current = repairRun;
  overridesRef.current = overrides;
  viewingRef.current = viewingVersionId;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current);
    };
  }, []);

  const notify = (severity, message) => setSnack({ severity, message, key: Date.now() });

  // ── Widget data (progressive, per widget, last-request-wins) ───────────────
  const fetchWidget = React.useCallback(async (widgetId, { refresh = false, values } = {}) => {
    const current = dashRef.current;
    if (!current) return;
    const seq = (seqRef.current[widgetId] || 0) + 1;
    seqRef.current[widgetId] = seq;
    // A widget that FAILED to build has a status_note and no binding: render it failed, never query it.
    const specWidget = ((current.spec && current.spec.widgets) || []).find((x) => x.widget_id === widgetId);
    if (specWidget && !specWidget.binding_hash && (specWidget.status_note || specWidget.status === 'FAILED')) {
      setWidgetData((prev) => ({ ...prev,
        [widgetId]: { loading: false, response: null, error: t('Dashboards.run.widgetVerdict.FAILED') } }));
      return;
    }
    setWidgetData((prev) => ({ ...prev, [widgetId]: { ...(prev[widgetId] || {}), loading: true, error: null } }));
    const controls = values || effectiveControls(current.spec, overridesRef.current);
    const res = await getWidgetData(id, widgetId,
      { versionId: current.version && current.version.id, controls, theme: THEME, refresh }, jwt);
    if (!mountedRef.current || seqRef.current[widgetId] !== seq) return;
    setWidgetData((prev) => ({
      ...prev,
      [widgetId]: res.ok
        ? { loading: false, error: null, response: res.data }
        : { loading: false, response: null,
          error: (res.data && res.data.message) || t('Dashboards.widget.loadFailed') },
    }));
  }, [id, jwt, t]);

  const fetchAllWidgets = React.useCallback((spec, opts = {}) => {
    ((spec && spec.widgets) || []).forEach((w) => { fetchWidget(w.widget_id, opts); });
  }, [fetchWidget]);

  // ── Dashboard load ─────────────────────────────────────────────────────────
  const loadDashboard = React.useCallback(async (versionId) => {
    setLoadState((s) => ({ ...s, loading: true, error: null }));
    const res = await getDashboard(id, versionId || null, jwt);
    if (!mountedRef.current) return;
    if (!res.ok && res.status === 404 && !versionId && runFromUrlRef.current) {
      setLoadState({ loading: false, error: null }); // generation pending → RunProgressPanel shell
      return;
    }
    if (!res.ok) {
      const key = res.status === 404 ? 'notFound' : res.status === 403 ? 'forbidden' : 'loadFailed';
      setLoadState({ loading: false, error: t(`Dashboards.view.${key}`) });
      return;
    }
    dashRef.current = res.data;
    setDash(res.data);
    setViewingVersionId(versionId || null);
    setWidgetData({});
    setDiagnoses({});
    diagnosisRequestedRef.current = new Set();
    setLoadState({ loading: false, error: null });
    fetchAllWidgets(res.data.spec);
  }, [id, jwt, t, fetchAllWidgets]);

  React.useEffect(() => {
    setOverrides({});
    loadDashboard(null);
  }, [loadDashboard]);

  React.useEffect(() => {
    let alive = true;
    getCapabilities(jwt).then((res) => { if (alive && res.ok) setCapabilities(res.data); });
    return () => { alive = false; };
  }, [jwt]);

  // A fresh "Create with AI" lands here with ?run=<runId>.
  React.useEffect(() => {
    const run = new URLSearchParams(location.search || '').get('run');
    if (run) setActiveRunId(run);
  }, [location.search]);

  const clearRunParam = React.useCallback(() => {
    if (new URLSearchParams(location.search || '').get('run')) history.replace(location.pathname);
  }, [history, location.pathname, location.search]);

  const onRunCompleted = React.useCallback(async (status) => {
    clearRunParam();
    if (!status.resultVersionId) return; // META edit: the answer stays in the panel (terminalNote)
    const loading = loadDashboard(null); // sets loading synchronously, so the panel can go now
    setActiveRunId(null);
    await loading;
    if (!mountedRef.current) return;
    setVersionsRefreshKey((k) => k + 1);
    const current = dashRef.current;
    const wasAddWidget = addWidgetRunRef.current != null && String(addWidgetRunRef.current) === String(status.runId);
    addWidgetRunRef.current = null;
    setActiveRunTitle(null);
    if (status.inputKind === 'NL_CREATE') {
      notify('success', t('Dashboards.run.completed'));
    } else if (wasAddWidget) {
      notify('success', t('Dashboards.addWidget.added', { n: current && current.version ? current.version.version_no : '' }));
    } else {
      notify('success', t('Dashboards.nlEdit.applied', { n: current && current.version ? current.version.version_no : '' }));
    }
    setActiveRunId(null);
  }, [clearRunParam, loadDashboard, t]);

  const onNlEditStale = React.useCallback(() => {
    notify('warning', t('Dashboards.edit.conflictReload'));
    loadDashboard(null);
  }, [loadDashboard, t]);

  // A run that FAILED on a stale base (terminalNote "STALE_BASE_VERSION…") is handled like a 409.
  const onRunFailed = React.useCallback((status) => {
    clearRunParam();
    if (String((status && status.terminalNote) || '').indexOf('STALE_BASE_VERSION') === 0) {
      notify('warning', t('Dashboards.edit.conflictRetry', {
        defaultValue: 'This dashboard changed while your edit was running. Reloaded the latest version — please retry.' }));
      loadDashboard(null);
      setVersionsRefreshKey((k) => k + 1);
      setActiveRunId(null);
    }
  }, [clearRunParam, loadDashboard, t]);

  // ── Widget repair (POST /{id}/widgets/{widgetId}/repair → run polled inline) ──
  const runBusy = !!activeRunId || !!repairRun || editPending;

  /** POST …/repair with a combined request (hint, suggestion, answers, goal, remember, allow_questions). */
  const startRepair = React.useCallback(async (widgetId, request, draft) => {
    const current = dashRef.current;
    if (!current || !current.version || repairRunRef.current) return;
    setRepairError(null);
    const res = await postRepairWidget(id, widgetId, current.version.id, request || {}, jwt);
    if (!mountedRef.current) return;
    if (res.ok && res.data && res.data.run_id != null) {
      const run = { widgetId, runId: res.data.run_id, draft: draft || null };
      repairRunRef.current = run;
      setRepairRun(run);
      const r = request || {};
      const hasKnowledge = !!(r.hint || r.suggestion_id || r.suggestion_hint || r.goal_override || (r.answers && r.answers.length));
      setKnowledgeSavedFor(hasKnowledge && r.remember !== false ? widgetId : null);
      if (draft) setRepairDrafts((prev) => ({ ...prev, [widgetId]: draft }));
      setRepairFailures((prev) => { const next = { ...prev }; delete next[widgetId]; return next; });
      return;
    }
    if (res.status === 409) {
      setStaleVersionId((res.data && res.data.current_version_id) || '?');
      return;
    }
    const msg = (res.data && (res.data.message || res.data.error)) || t('Dashboards.repair.startFailed');
    setRepairError(msg);
    setRepairDialog((d) => d || { widgetId, mode: 'guided' }); // show the error where the user can act on it
  }, [id, jwt, t]);

  const onRepairTerminal = React.useCallback(async (status) => {
    const run = repairRunRef.current;
    if (!run) return;
    if (status.status === 'AWAITING_HITL') { // the repair asks instead of failing: answer it in the dialog
      setRepairCheckpoint(status.checkpoint || null);
      setRepairDialog((d) => (d && d.widgetId === run.widgetId ? d : { widgetId: run.widgetId, mode: 'guided' }));
      return;
    }
    repairRunRef.current = null;
    setRepairRun(null);
    setRepairCheckpoint(null);
    const item = (status.progress || []).find((p) => p.widget_id === run.widgetId) || {};
    if (status.status === 'COMPLETED') {
      await loadDashboard(null);
      if (!mountedRef.current) return;
      setVersionsRefreshKey((k) => k + 1);
      setRepairDialog((d) => (d && d.widgetId === run.widgetId ? null : d));
      setRepairDrafts((prev) => { const next = { ...prev }; delete next[run.widgetId]; return next; });
      const degraded = item.verdict === 'DEGRADED' || !!status.terminalNote;
      const title = (item.title || run.widgetId);
      notify(degraded ? 'warning' : 'success', degraded
        ? t('Dashboards.repair.degraded', { title, note: status.terminalNote || item.status_note || '' })
        : t('Dashboards.repair.completed', { title }));
      return;
    }
    if (String(status.terminalNote || '').indexOf('STALE_BASE_VERSION') === 0) {
      notify('warning', t('Dashboards.edit.conflictRetry'));
      await loadDashboard(null);
      setVersionsRefreshKey((k) => k + 1);
    }
    setRepairFailures((prev) => ({ ...prev, [run.widgetId]: {
      stage: status.terminalStage || item.stage, note: status.terminalNote || item.status_note,
      lastSql: item.last_sql || null, diagnosis: item.diagnosis || null } }));
    // keep / reopen the form, pre-filled, so the user can iterate
    setRepairDialog((d) => (d && d.widgetId === run.widgetId ? d : { widgetId: run.widgetId, mode: 'guided' }));
  }, [loadDashboard, t]);

  const { status: repairStatus, restart: restartRepairPolling } = useRunPolling(repairRun && repairRun.runId, jwt, onRepairTerminal);

  const onRepairCheckpointResolved = React.useCallback(() => {
    setRepairCheckpoint(null);
    restartRepairPolling();
  }, [restartRepairPolling]);

  /** Lazy diagnosis for failed widgets that have none (legacy specs, crashed builds, runtime errors). */
  const requestDiagnosis = React.useCallback(async (widget) => {
    const current = dashRef.current;
    const wid = widget.widget_id;
    if (!current || !current.version || diagnosisRequestedRef.current.has(wid)) return;
    diagnosisRequestedRef.current.add(wid);
    setDiagnoses((prev) => ({ ...prev, [wid]: { loading: true, data: null } }));
    const res = await diagnoseWidget(id, wid, current.version.id, jwt);
    if (!mountedRef.current) return;
    setDiagnoses((prev) => ({ ...prev, [wid]: { loading: false, data: res.ok && res.data ? res.data.diagnosis : null } }));
  }, [id, jwt]);

  const diagnosisFor = (w) => (repairFailures[w.widget_id] && repairFailures[w.widget_id].diagnosis)
    || (w.build_failure && w.build_failure.diagnosis)
    || (diagnoses[w.widget_id] && diagnoses[w.widget_id].data) || null;

  const onRepair = React.useCallback((widget, action) => {
    const a = typeof action === 'string' ? { type: action === 'hint' ? 'guided' : action } : (action || {});
    setRepairError(null);
    if (a.type === 'retry') startRepair(widget.widget_id, {}, null);
    else if (a.type === 'suggestion' && a.suggestion) {
      startRepair(widget.widget_id, { suggestion_id: a.suggestion.id },
        { ...emptyDraft(), suggestionId: a.suggestion.id, suggestionText: a.suggestion.hint || '' });
    } else setRepairDialog({ widgetId: widget.widget_id, mode: a.type === 'expert' ? 'expert' : 'guided' });
  }, [startRepair]);

  const onStaleReload = React.useCallback(() => {
    setStaleVersionId(null);
    setRepairError(null);
    loadDashboard(null);
    setVersionsRefreshKey((k) => k + 1);
  }, [loadDashboard]);

  const isCurrentVersion = !viewingVersionId;
  const canEdit = !!(dash && dash.can_edit) && isCurrentVersion;

  // ── EditOps (serialized; spec replaced only from the server response) ──────
  const applyOps = React.useCallback((ops) => {
    const run = async () => {
      const before = dashRef.current;
      if (!before || !ops.length || viewingRef.current || !before.can_edit) return false;
      setEditPending(true);
      const res = await postUxEdits(id, before.version.id, ops, jwt);
      if (!mountedRef.current) return false;
      setEditPending(false);
      if (res.ok && res.data && res.data.spec) {
        const after = {
          ...before,
          dashboard: { ...before.dashboard, current_version_id: res.data.version && res.data.version.id },
          version: res.data.version,
          spec: res.data.spec,
        };
        dashRef.current = after;
        setDash(after);
        setVersionsRefreshKey((k) => k + 1);
        // Re-query only widgets whose binding/visual changed, new widgets, and widgets bound to a control whose default moved.
        const oldWidgets = {};
        (before.spec.widgets || []).forEach((w) => { oldWidgets[w.widget_id] = w; });
        const oldDefaults = effectiveControls(before.spec, {});
        const newDefaults = effectiveControls(after.spec, {});
        const changedControls = Object.keys(newDefaults).filter((k) => !same(oldDefaults[k], newDefaults[k]));
        (after.spec.widgets || []).forEach((w) => {
          const o = oldWidgets[w.widget_id];
          const changed = !o || o.binding_hash !== w.binding_hash || !same(o.visual, w.visual)
            || changedControls.some((cid) => widgetUsesControl(w, cid) && !has(overridesRef.current, cid));
          if (changed) fetchWidget(w.widget_id);
        });
        return true;
      }
      if (res.status === 409) {
        notify('warning', t('Dashboards.edit.conflictReload'));
        pendingLayoutRef.current = {};
        await loadDashboard(null);
        return false;
      }
      notify('error', (res.data && (res.data.message || res.data.error)) || t('Dashboards.edit.failed'));
      setGridResetKey((k) => k + 1);
      return false;
    };
    const p = editChainRef.current.then(run, run);
    editChainRef.current = p.catch(() => false);
    return p;
  }, [id, jwt, t, fetchWidget, loadDashboard]);

  const onChangeVizType = (widgetId, vizType) => applyOps([editOps.setVizType(widgetId, vizType)]);

  const flushLayout = React.useCallback(() => {
    layoutTimerRef.current = null;
    const pending = pendingLayoutRef.current;
    pendingLayoutRef.current = {};
    const current = dashRef.current;
    if (!current) return;
    const ops = (current.spec.widgets || [])
      .filter((w) => pending[w.widget_id] && !same(pending[w.widget_id], {
        x: w.layout && w.layout.x, y: w.layout && w.layout.y, w: w.layout && w.layout.w, h: w.layout && w.layout.h,
      }))
      .map((w) => editOps.setLayout(w.widget_id, pending[w.widget_id]));
    if (ops.length) applyOps(ops);
  }, [applyOps]);

  const onLayoutCommit = React.useCallback((layout) => {
    (layout || []).forEach((item) => {
      pendingLayoutRef.current[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h };
    });
    if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current);
    layoutTimerRef.current = setTimeout(flushLayout, LAYOUT_DEBOUNCE_MS);
  }, [flushLayout]);

  // ── Controls (viewer-local unless saved as default) ────────────────────────
  const onControlChange = (controlId, value) => {
    const next = { ...overridesRef.current, [controlId]: value };
    overridesRef.current = next;
    setOverrides(next);
    const values = effectiveControls(dashRef.current && dashRef.current.spec, next);
    ((dashRef.current && dashRef.current.spec.widgets) || [])
      .filter((w) => widgetUsesControl(w, controlId))
      .forEach((w) => fetchWidget(w.widget_id, { values }));
  };

  const dirtyIds = React.useMemo(() => {
    if (!dash) return [];
    return (dash.spec.global_controls || [])
      .filter((c) => has(overrides, c.control_id) && !same(overrides[c.control_id], c.default))
      .map((c) => c.control_id);
  }, [dash, overrides]);

  const onResetControls = () => {
    const touched = Object.keys(overridesRef.current);
    overridesRef.current = {};
    setOverrides({});
    const values = effectiveControls(dashRef.current.spec, {});
    (dashRef.current.spec.widgets || [])
      .filter((w) => touched.some((cid) => widgetUsesControl(w, cid)))
      .forEach((w) => fetchWidget(w.widget_id, { values }));
  };

  const onSaveDefaults = async () => {
    const ids = dirtyIds.slice();
    const values = { ...overridesRef.current };
    const okSaved = await applyOps(ids.map((cid) => editOps.setControlValue(cid, values[cid])));
    if (okSaved && mountedRef.current) {
      const next = { ...overridesRef.current };
      ids.forEach((cid) => { if (same(next[cid], values[cid])) delete next[cid]; });
      overridesRef.current = next;
      setOverrides(next);
      notify('success', t('Dashboards.controls.defaultsSaved'));
    }
  };

  // ── Versions ───────────────────────────────────────────────────────────────
  const onViewVersion = (versionId) => {
    setVersionsOpen(false);
    loadDashboard(versionId);
  };

  const onRevert = async (versionId) => {
    const current = dashRef.current;
    if (!current) return;
    const res = await revertToVersion(id, versionId, jwt);
    if (!mountedRef.current) return;
    if (res.ok && res.data && res.data.spec) {
      const after = {
        ...current,
        dashboard: { ...current.dashboard, current_version_id: res.data.version && res.data.version.id },
        version: res.data.version, spec: res.data.spec,
      };
      dashRef.current = after;
      setDash(after);
      setViewingVersionId(null);
      setWidgetData({});
      setVersionsRefreshKey((k) => k + 1);
      setVersionsOpen(false);
      fetchAllWidgets(after.spec);
      notify('success', t('Dashboards.versions.reverted', { n: res.data.version && res.data.version.version_no }));
    } else if (res.status === 409) {
      notify('warning', t('Dashboards.edit.conflictReload'));
      loadDashboard(null);
    } else {
      notify('error', t('Dashboards.versions.revertFailed'));
    }
  };

  const onMetaSaved = (updated) => {
    if (!updated) return;
    setDash((d) => (d ? { ...d, dashboard: { ...d.dashboard, ...updated } } : d));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const pendingRunId = activeRunId || runFromUrl;
  if (!dash && pendingRunId && !loadState.loading && !loadState.error) {
    return (
      <Box sx={PAGE_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tooltip title={t('Dashboards.view.backToList')}>
            <IconButton onClick={() => history.push('/data_analytics/dashboards')}><ArrowBackIcon /></IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>{t('Dashboards.run.title')}</Typography>
          {DASHBOARD_MOCK_MODE && <Chip size="small" color="secondary" label={t('Dashboards.view.mockMode')} />}
        </Box>
        <RunProgressPanel
          key={pendingRunId}
          runId={pendingRunId}
          jwt={jwt}
          onCompleted={onRunCompleted}
          onFailed={onRunFailed}
          onDismiss={() => history.push('/data_analytics/dashboards')}
        />
        <Snackbar open={!!snack} key={snack && snack.key} autoHideDuration={5000} onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : <span />}
        </Snackbar>
      </Box>
    );
  }
  if (loadState.loading && !dash) {
    return <Box sx={{ px: 6, pb: 6, pt: CONTENT_TOP_OFFSET, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (!dash) {
    return (
      <Box sx={{ px: 3, pb: 3, pt: CONTENT_TOP_OFFSET }}>
        <Alert severity="error" action={<Button onClick={() => history.push('/data_analytics/dashboards')}>{t('Dashboards.view.backToList')}</Button>}>
          {loadState.error || t('Dashboards.view.notFound')}
        </Alert>
      </Box>
    );
  }

  const spec = dash.spec || {};
  const widgets = spec.widgets || [];
  const values = effectiveControls(spec, overrides);
  const plotlyMismatch = capabilities && capabilities.plotlyjs_version
    && majorMinor(capabilities.plotlyjs_version) !== majorMinor(CLIENT_PLOTLYJS_VERSION);

  const renderWidget = (w, editable) => (
    <WidgetFrame
      widget={w}
      data={widgetData[w.widget_id]}
      capabilities={capabilities}
      canEdit={editable}
      editPending={editPending}
      onChangeVizType={onChangeVizType}
      onRefresh={(wid) => fetchWidget(wid, { refresh: true })}
      canRepair={canEdit}
      repairing={repairRun && repairRun.widgetId === w.widget_id
        ? { stage: repairStatus && repairStatus.currentStage } : null}
      repairDisabled={runBusy}
      onRepair={onRepair}
      diagnosis={diagnosisFor(w)}
      diagnosisLoading={!!(diagnoses[w.widget_id] && diagnoses[w.widget_id].loading)}
      onNeedDiagnosis={requestDiagnosis}
      extraActions={(
        <Tooltip title={t('Dashboards.widget.howIGotThis')}>
          <IconButton size="small" onClick={() => setProvenanceWidget(w)} aria-label={t('Dashboards.widget.howIGotThis')}>
            <PsychologyAltOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    />
  );

  return (
    <Box sx={PAGE_SX}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Tooltip title={t('Dashboards.view.backToList')}>
          <IconButton onClick={() => history.push('/data_analytics/dashboards')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0, flexShrink: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" noWrap sx={{ fontWeight: 700 }}>
              {(dash.dashboard && dash.dashboard.title) || spec.title}
            </Typography>
            {canEdit && (
              <Tooltip title={t('Dashboards.meta.rename')}>
                <IconButton size="small" onClick={() => setRenameOpen(true)}><EditOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Box>
          {spec.description && (
            <Typography variant="body2" color="text.secondary" noWrap>{spec.description}</Typography>
          )}
        </Box>
        {dash.version && (
          <Chip size="small" variant="outlined" label={t('Dashboards.versions.versionNo', { n: dash.version.version_no })} />
        )}
        {DASHBOARD_MOCK_MODE && <Chip size="small" color="secondary" label={t('Dashboards.view.mockMode')} />}
        {editPending && <CircularProgress size={18} />}
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && (
          <ShareToggle dashboard={dash.dashboard} widgets={widgets} jwt={jwt} disabled={editPending} onSaved={onMetaSaved} />
        )}
        {!canEdit && dash.dashboard && (
          <Chip size="small" label={String(dash.dashboard.visibility).toUpperCase() === 'TENANT'
            ? t('Dashboards.meta.visibilityTenant') : t('Dashboards.meta.visibilityPrivate')} />
        )}
        <Tooltip title={t('Dashboards.view.refreshAll')}>
          <IconButton onClick={() => fetchAllWidgets(spec, { refresh: true })}><RefreshIcon /></IconButton>
        </Tooltip>
        {canEdit && (
          <Button startIcon={<AddIcon />} disabled={runBusy} onClick={() => setAddWidgetOpen(true)} sx={{ textTransform: 'none' }}>
            {t('Dashboards.addWidget.open')}
          </Button>
        )}
        <Button startIcon={<HistoryIcon />} onClick={() => setVersionsOpen(true)}>{t('Dashboards.versions.open')}</Button>
      </Box>

      {staleVersionId != null && !repairDialogWidgetId && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={onStaleReload}>{t('Dashboards.repair.reload')}</Button>}>
          {t('Dashboards.repair.stale', { id: staleVersionId })}
        </Alert>
      )}

      {!isCurrentVersion && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => loadDashboard(null)}>{t('Dashboards.versions.backToCurrent')}</Button>}>
          {t('Dashboards.versions.viewingOld', { n: dash.version && dash.version.version_no })}
        </Alert>
      )}
      {plotlyMismatch && !versionWarningDismissed && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setVersionWarningDismissed(true)}>
          {t('Dashboards.view.plotlyMismatch', { server: capabilities.plotlyjs_version, client: CLIENT_PLOTLYJS_VERSION })}
        </Alert>
      )}

      <ControlsBar
        controls={spec.global_controls}
        values={values}
        dirtyIds={dirtyIds}
        canEdit={canEdit}
        editPending={editPending}
        onChange={onControlChange}
        onSaveDefaults={onSaveDefaults}
        onReset={onResetControls}
      />

      {canEdit && (
        <NlEditBox
          dashboardId={id}
          baseVersionId={dash.version && dash.version.id}
          jwt={jwt}
          disabled={runBusy}
          onRunStarted={(runId) => setActiveRunId(runId)}
          onStale={onNlEditStale}
        />
      )}
      {activeRunId && (
        <RunProgressPanel
          key={activeRunId}
          runId={activeRunId}
          jwt={jwt}
          title={activeRunTitle || undefined}
          onCompleted={onRunCompleted}
          onFailed={onRunFailed}
          onDismiss={() => { clearRunParam(); setActiveRunId(null); setActiveRunTitle(null); addWidgetRunRef.current = null; }}
        />
      )}

      {widgets.length === 0 ? (activeRunId ? null : (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Dashboards.view.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('Dashboards.view.emptyBody')}</Typography>
        </Box>
      )) : (
        <DashboardGrid
          widgets={widgets}
          canEdit={canEdit}
          renderWidget={renderWidget}
          onLayoutCommit={onLayoutCommit}
          resetKey={gridResetKey}
        />
      )}

      <VersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        dashboardId={id}
        jwt={jwt}
        currentVersionId={(dash.dashboard && dash.dashboard.current_version_id)
          || (isCurrentVersion && dash.version ? dash.version.id : null)}
        viewingVersionId={dash.version && dash.version.id}
        canEdit={!!dash.can_edit}
        onView={onViewVersion}
        onRevert={onRevert}
        refreshKey={versionsRefreshKey}
      />
      <ProvenanceDrawer
        open={!!provenanceWidget}
        onClose={() => setProvenanceWidget(null)}
        dashboardId={id}
        widget={provenanceWidget}
        versionId={dash.version && dash.version.id}
        jwt={jwt}
      />
      {(() => {
        const rw = widgets.find((x) => x.widget_id === repairDialogWidgetId) || null;
        const runHere = !!repairRun && repairRun.widgetId === repairDialogWidgetId;
        return (
          <WidgetRepairDialog
            open={!!repairDialog}
            mode={repairDialog ? repairDialog.mode : 'guided'}
            widget={rw}
            diagnosis={rw ? diagnosisFor(rw) : null}
            diagnosisLoading={!!(diagnoses[repairDialogWidgetId] && diagnoses[repairDialogWidgetId].loading)}
            draft={(runHere && repairRun.draft) || repairDrafts[repairDialogWidgetId] || null}
            failure={repairFailures[repairDialogWidgetId] || null}
            running={runHere && !repairCheckpoint}
            runStage={repairStatus && repairStatus.currentStage}
            checkpoint={runHere ? repairCheckpoint : null}
            jwt={jwt}
            busy={runBusy && !runHere}
            error={repairError}
            staleVersionId={staleVersionId}
            knowledgeSaved={knowledgeSavedFor === repairDialogWidgetId}
            onSubmit={(request, draft) => startRepair(repairDialogWidgetId, request, draft)}
            onClose={() => { setRepairDialog(null); setRepairError(null); }}
            onReload={onStaleReload}
            onCheckpointResolved={onRepairCheckpointResolved}
          />
        );
      })()}
      <AddWidgetDialog
        open={addWidgetOpen}
        onClose={() => setAddWidgetOpen(false)}
        dashboardId={id}
        baseVersionId={dash.version && dash.version.id}
        jwt={jwt}
        disabled={runBusy}
        onRunStarted={(runId) => {
          setAddWidgetOpen(false);
          addWidgetRunRef.current = runId;
          setActiveRunTitle(t('Dashboards.addWidget.running'));
          setActiveRunId(runId);
        }}
        onStale={(currentVersionId) => { setAddWidgetOpen(false); setStaleVersionId(currentVersionId || '?'); }}
      />
      <RenameDialog open={renameOpen} onClose={() => setRenameOpen(false)} dashboard={dash.dashboard} jwt={jwt} onSaved={onMetaSaved} />

      <Snackbar open={!!snack} key={snack && snack.key} autoHideDuration={5000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}
